import React, { useState, useEffect, useReducer, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { useDispatch } from 'react-redux';
import { debounce } from 'lodash';
import * as actions from './redux/actions';
import ViewerWrapper from './ViewerWrapper';
import { Allotment } from 'allotment';
import { setSidebarOpen } from 'features/common/redux/navbarLayout';
import { useScrollToHighlight } from 'features/viewing/AcusoftViewer/AcusoftViewerControl/hooks/useScrollToHighlight';
import SearchWithList from 'features/common/SearchWithList';
import { searchReducer, initialSearchState } from './RightSidebarTabList/sideBarSearchReducer';
import { IconButton } from '@mui/material';
import { Close } from '@mui/icons-material';
import T from 'i18n';
import 'allotment/dist/style.css';
import './TableAndViewer.css';
import {
  selectParams,
  selectMatchedUrl,
  selectCurrentCaseId,
  selectCurrentSelectedFileMetaData,
  selectUserSettingSearchMode,
  selectUserSettingDocFinder,
  selectUserSettingAllAnnotations,
  selectUserSettingPageRefs,
  selectedDocument,
  selectedAnnotation,
  selectFileToCompare,
  selectUserAiTools,
  selectDocFinderFilter,
  selectGlobalSideBarOpen,
  selectGlobalSummaryOpen,
  selectAskAiSearchType,
  selectViewerControl,
  selectIsLocalUploadDrawerOpen,
} from 'common/selectors';
import { isMobile } from 'utils/device';
import TablePortion from './TablePortion';
import SideSummaryDisplay from './SideSummaryDisplay';
import { clearClickedSourceState } from './redux/setAISearchState';
import { setAskAiSearchType } from './redux/setAskAiSearchType';
import { setFileToCompare } from './redux/setFileToCompare';
import { setSummaryOpen } from 'features/common/redux/navbarLayout';
import { setAiToolsFilters } from './redux/setAiToolsFilters';
import { FocusViewBridgeProvider } from './FocusViewBridge';

export const TableAndViewer = (props) => {
  const {
    file,
    isSummaryOpen,
    askAiSearchType,
    fileToCompare,
    isAiTools, // detemines if the url has 'ai-tools' for the main page
    isAskAIEnabled,
    currentlySelectedRow,
    viewerControl,
    fileType: propsFileType,
    selectedDocument: propsSelectedDocument,
    actions: propsActions,
  } = props;
  const dispatch = useDispatch();
  const [fileType, setFileType] = useState(propsFileType);
  const [allHighlights] = useState([]);
  const [isSearchPaneOpen, setIsSearchPaneOpen] = useState(false);
  const [searchState, searchDispatch] = useReducer(searchReducer, initialSearchState);
  const currentSearchRequest = useRef(null);
  const [selectedSearchIndex, setSelectedSearchIndex] = useState(null);
  // Delay flag to avoid showing premature "no results" if backend search is slow
  const [noResultsDelayPassed, setNoResultsDelayPassed] = useState(true);
  const noResultsDelayTimerRef = useRef(null);
  // Use the useScrollToHighlight hook
  const { scrollSelectedHighlight, scrollToHighlight } = useScrollToHighlight({
    viewerControl: file ? viewerControl : null,
  });

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (!urlParams.get('askAI') && !urlParams.get('fileAskAI') && isAskAIEnabled) {
      dispatch(setSummaryOpen(false));
      dispatch(setAiToolsFilters({}));
      dispatch(setAskAiSearchType(null));
    }
  });
  useEffect(() => {
    if (!isSummaryOpen && !isAiTools) {
      dispatch(clearClickedSourceState(null));
      dispatch(setAskAiSearchType(null));
    }
    return () => {
      const urlParams = new URLSearchParams(window.location.search);
      if (!window.location.pathname.includes('ai-tools') || !urlParams.get('askAI')) {
        dispatch(clearClickedSourceState());
        dispatch(setFileToCompare(null));
      }
    };
  }, [dispatch, isAiTools, isSummaryOpen]);

  useEffect(() => {
    if (propsFileType !== fileType) {
      setFileType(propsFileType);
    } else if (file && !propsSelectedDocument && !propsFileType) {
      propsActions.fetchFileDetails({ fileId: file }).then((res) => {
        setFileType(res.type);
      });
    }
  }, [propsFileType, fileType, file, propsSelectedDocument, propsActions]);

  // Handler for search button click
  const handleSearchClick = () => {
    setIsSearchPaneOpen(!isSearchPaneOpen);
    dispatch(setSidebarOpen(false));
  };

  // Handler for closing search pane
  const handleCloseSearchPane = () => {
    setIsSearchPaneOpen(false);
    handleClearSearch();
  };

  // Debounced search function
  const debouncedSearch = useMemo(
    () =>
      debounce(async (query) => {
        // Check if query contains non-alphabetical characters (excluding spaces)
        const hasNonAlphabetical = /[^a-zA-Z\s]/.test(query);

        // Only search if there are at least 3 characters, unless query contains non-alphabetical characters
        // But don't process if query is empty or contains only spaces
        if (!query.trim() || (!hasNonAlphabetical && query.trim().length < 3)) {
          searchDispatch({
            type: 'UPDATE_SEARCH_STATE',
            payload: {
              results: [],
              loading: false,
            },
          });
          return;
        }

        try {
          searchDispatch({
            type: 'UPDATE_SEARCH_STATE',
            payload: {
              loading: true,
              error: null,
            },
          });
          if (currentSearchRequest.current && !currentSearchRequest.current.isComplete) {
            currentSearchRequest.current.cancel();
          }
          const searchRequest = viewerControl.search(query.trim());
          currentSearchRequest.current = searchRequest;

          searchRequest.on('PartialSearchResultsAvailable', ({ partialSearchResults }) => {
            if (partialSearchResults && partialSearchResults.length > 0) {
              searchDispatch({ type: 'SET_SEARCH_RESULTS', payload: partialSearchResults });
            }
          });

          searchRequest.on('SearchCompleted', ({ completedSearchResults }) => {
            // Clear any pending delay timer; results (even empty) now considered final
            if (noResultsDelayTimerRef.current) {
              clearTimeout(noResultsDelayTimerRef.current);
              noResultsDelayTimerRef.current = null;
            }
            setNoResultsDelayPassed(true);
            searchDispatch({
              type: 'UPDATE_SEARCH_STATE',
              payload: {
                results: completedSearchResults || [],
                loading: false,
                hasSearched: true,
              },
            });
          });

          searchRequest.on('SearchCancelled', () => {
            searchDispatch({ type: 'SET_LOADING', payload: false });
          });

          searchRequest.on('SearchFailed', () => {
            if (noResultsDelayTimerRef.current) {
              clearTimeout(noResultsDelayTimerRef.current);
              noResultsDelayTimerRef.current = null;
            }
            setNoResultsDelayPassed(true);
            searchDispatch({
              type: 'UPDATE_SEARCH_STATE',
              payload: {
                results: [],
                loading: false,
                error: 'An error occurred while searching. Please try again.',
                hasSearched: true,
              },
            });
          });
        } catch (err) {
          if (noResultsDelayTimerRef.current) {
            clearTimeout(noResultsDelayTimerRef.current);
            noResultsDelayTimerRef.current = null;
          }
          setNoResultsDelayPassed(true);
          searchDispatch({
            type: 'UPDATE_SEARCH_STATE',
            payload: {
              results: [],
              loading: false,
              error: `An error occurred while searching. Please try again. ${err}`,
              hasSearched: true,
            },
          });
        }
      }, 500), // 500ms debounce time
    [viewerControl],
  );

  const handleClearSearch = useCallback(() => {
    searchDispatch({ type: 'CLEAR_SEARCH' });
    setSelectedSearchIndex(null);
    setNoResultsDelayPassed(true);
    if (noResultsDelayTimerRef.current) {
      clearTimeout(noResultsDelayTimerRef.current);
      noResultsDelayTimerRef.current = null;
    }

    // Cancel any ongoing search request
    if (currentSearchRequest.current && !currentSearchRequest.current.isComplete) {
      currentSearchRequest.current.cancel();
    }
    if (viewerControl && typeof viewerControl.clearSearch === 'function') {
      try {
        viewerControl.clearSearch();
      } catch (err) {
        console.error('Error clearing search:', err);
      }
    }
  }, [viewerControl]);

  const handleSearchInputChange = useCallback(
    (event) => {
      const newValue = event.target.value;

      if (newValue === '' && searchState.searchInput !== '') {
        handleClearSearch();
      } else {
        searchDispatch({ type: 'SET_SEARCH_INPUT', payload: newValue });
      }
    },
    [handleClearSearch, searchState.searchInput],
  );

  const handleResultClick = useCallback(
    (result, index) => {
      setSelectedSearchIndex(index);
      if (viewerControl && result.pageNumber) {
        try {
          // Navigate to the page containing the result
          if (typeof viewerControl.setCurrentPage === 'function') {
            viewerControl.setCurrentPage(result.pageNumber);
          } else if (typeof viewerControl.setPageNumber === 'function') {
            viewerControl.setPageNumber(result.pageNumber);
          }

          // Scroll to the result on the page
          if (typeof viewerControl.scrollTo === 'function') {
            viewerControl.scrollTo(result);
          } else if (typeof viewerControl.scrollToAsync === 'function') {
            viewerControl
              .scrollToAsync(result.location)
              .then(() => {
                // Add highlight to the search result
                if (typeof viewerControl.addMark === 'function') {
                  viewerControl.addMark({
                    type: 'highlight',
                    color: '#AFD0F9',
                    ...result,
                  });
                }
              })
              .catch((err) => {
                console.error('Failed to scroll to result:', err);
              });
          }
        } catch (err) {
          console.error('Error navigating to search result:', err);
        }
      }
    },
    [viewerControl],
  );

  // Clear search state when file changes
  useEffect(() => {
    if (file) {
      handleClearSearch();
      setIsSearchPaneOpen(false);
    }
  }, [file, handleClearSearch]);

  // Trigger search when searchInput changes
  useEffect(() => {
    // Check if query contains non-alphabetical characters (excluding spaces)
    const hasNonAlphabetical = /[^a-zA-Z\s]/.test(searchState.searchInput);
    const shouldSearch = hasNonAlphabetical || searchState.searchInput.trim().length >= 3;

    if (shouldSearch && searchState.searchInput.trim()) {
      // When initiating a new search, start delay window to suppress early no-results
      if (noResultsDelayTimerRef.current) {
        clearTimeout(noResultsDelayTimerRef.current);
      }
      setNoResultsDelayPassed(false);
      // Allow e.g. 2.5s for backend aggregation before we show empty-state (adjust as needed)
      noResultsDelayTimerRef.current = setTimeout(() => {
        setNoResultsDelayPassed(true);
        noResultsDelayTimerRef.current = null;
      }, 2500);
      debouncedSearch(searchState.searchInput);
    } else if (searchState.searchInput.trim().length === 0) {
      searchDispatch({
        type: 'UPDATE_SEARCH_STATE',
        payload: {
          results: [],
          loading: false,
          error: null,
        },
      });
      if (noResultsDelayTimerRef.current) {
        clearTimeout(noResultsDelayTimerRef.current);
        noResultsDelayTimerRef.current = null;
      }
      setNoResultsDelayPassed(true);
    } else {
      searchDispatch({
        type: 'UPDATE_SEARCH_STATE',
        payload: {
          results: [],
          loading: false,
        },
      });
      if (noResultsDelayTimerRef.current) {
        clearTimeout(noResultsDelayTimerRef.current);
        noResultsDelayTimerRef.current = null;
      }
      setNoResultsDelayPassed(true);
    }

    // Cleanup on unmount
    return () => {
      debouncedSearch.cancel();
      if (currentSearchRequest.current && !currentSearchRequest.current.isComplete) {
        currentSearchRequest.current.cancel();
      }
      if (noResultsDelayTimerRef.current) {
        clearTimeout(noResultsDelayTimerRef.current);
        noResultsDelayTimerRef.current = null;
      }
    };
  }, [searchState.searchInput, debouncedSearch]);

  // Determine which panes are visible to construct Allotment sizes correctly
  const showCompareViewer =
    fileToCompare && Object.keys(fileToCompare).length > 0 && (isSummaryOpen || isAiTools) && file;
  // Compute panel sizes first, then derive visibility helpers from them to avoid TDZ
  const getPanelSizes = () => {
    if (showCompareViewer && isAiTools) return [35, 30, 35];
    if (showCompareViewer) return [0, 30, 35];
    if (!file && isSummaryOpen && !isAiTools) return [40, 60, 0];
    if (file && isSummaryOpen && !isAiTools) return [20, 40, 40];
    if (!file) return [100, 0, 0];
    if (isSummaryOpen && !isAiTools) return [25, 37, 38];
    if (isAiTools && isSummaryOpen) return [38, 25, 37];
    if (!isAskAIEnabled && isSummaryOpen) return [25, 37, 38];
    return [50, 0, 50];
  };

  const [tablePanelSize, summaryPanelSize, viewerPanelSize] = getPanelSizes();

  // Build panes and sizes together to keep ordering in sync with defaultSizes
  const panes = [];
  const sizes = [];
  // Table pane is always rendered (at size 0 during compare) to keep Allotment stable
  sizes.push(tablePanelSize);
  panes.push(
    <Allotment.Pane key="pane-table" minSize={10}>
      <TablePortion {...props} />
    </Allotment.Pane>,
  );
  // Compare viewer (always last in original ordering)
  if (showCompareViewer) {
    sizes.push(viewerPanelSize);
    panes.push(
      <Allotment.Pane key="pane-viewer-compare" minSize={100}>
        <ViewerWrapper
          fileId={fileToCompare?.id}
          compareFileName={fileToCompare?.name}
          fileType={fileToCompare?.type}
          fileData={fileToCompare}
          isFileToCompare={true}
          isDocFinder={props.isDocFinder}
          canShowSummaryPane={!showCompareViewer}
        />
      </Allotment.Pane>,
    );
  }

  const shouldRenderSummaryPane = isAiTools ? isSummaryOpen && !showCompareViewer : isSummaryOpen;
  const shouldRenderMainViewerPane = file || viewerPanelSize > 0;

  if (!askAiSearchType) {
    // Viewer then Summary (so summary opens on the right of viewer)
    if (shouldRenderMainViewerPane) {
      sizes.push(viewerPanelSize);
      panes.push(
        <Allotment.Pane key="pane-viewer-main" minSize={100}>
          {file && (
            <ViewerWrapper
              fileId={file}
              fileType={fileType}
              isDocFinder={props.isDocFinder}
              canShowSummaryPane={!showCompareViewer}
              onSearchClick={handleSearchClick}
            />
          )}
        </Allotment.Pane>,
      );
    }
    if (isSearchPaneOpen && shouldRenderMainViewerPane) {
      sizes.push(300);
      panes.push(
        <Allotment.Pane key="pane-search" minSize={200}>
          <div
            style={{
              height: '100%',
              backgroundColor: 'white',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Top bar with close button */}
            <div
              style={{
                height: '38px',
                backgroundColor: '#F7F7F8',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0 10px 0 16px',
                borderBottom: '1px solid #e0e0e0',
              }}
            >
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: '#6c757d',
                  lineHeight: 1,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {T.translate('generic.search')}
              </span>
              <IconButton
                size="small"
                onClick={handleCloseSearchPane}
                sx={{
                  padding: '4px',
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                }}
              >
                <Close fontSize="small" />
              </IconButton>
            </div>
            {/* Search content */}
            <div style={{ flex: 1, overflow: 'hidden', padding: '10px 0' }}>
              <SearchWithList
                searchInput={searchState.searchInput}
                searchResults={searchState.searchResults}
                isLoading={searchState.isLoading}
                error={searchState.searchError}
                selectedIndex={selectedSearchIndex}
                hasSearched={searchState.hasSearched}
                noResultsDelayPassed={noResultsDelayPassed}
                onSearchInputChange={handleSearchInputChange}
                onClearSearch={handleClearSearch}
                onResultClick={handleResultClick}
              />
            </div>
          </div>
        </Allotment.Pane>,
      );
    }
    if (shouldRenderSummaryPane) {
      sizes.push(summaryPanelSize);
      panes.push(
        <Allotment.Pane key="pane-summary" minSize={100}>
          {isSummaryOpen && (
            <SideSummaryDisplay
              open={isSummaryOpen}
              isAiTab={isAiTools}
              isAskAIEnabled={isAskAIEnabled}
              selectedHighlight={scrollSelectedHighlight}
              highlights={allHighlights}
              scrollToHighlight={scrollToHighlight}
              fileData={file ? currentlySelectedRow : null}
              viewerControl={file ? viewerControl : null}
              gridView={true}
            />
          )}
        </Allotment.Pane>,
      );
    }
  } else {
    // Summary then Viewer (askAiSearchType case)
    if (shouldRenderSummaryPane) {
      sizes.push(summaryPanelSize);
      panes.push(
        <Allotment.Pane key="pane-summary" minSize={100}>
          {isSummaryOpen && (
            <SideSummaryDisplay
              open={isSummaryOpen}
              isAiTab={isAiTools}
              isAskAIEnabled={isAskAIEnabled}
              selectedHighlight={scrollSelectedHighlight}
              highlights={allHighlights}
              scrollToHighlight={scrollToHighlight}
              fileData={file ? currentlySelectedRow : null}
              viewerControl={file ? viewerControl : null}
              gridView={true}
            />
          )}
        </Allotment.Pane>,
      );
    }
    if (shouldRenderMainViewerPane) {
      sizes.push(viewerPanelSize);
      panes.push(
        <Allotment.Pane key="pane-viewer-main" minSize={100}>
          {file && (
            <ViewerWrapper
              fileId={file}
              fileType={fileType}
              isDocFinder={props.isDocFinder}
              canShowSummaryPane={!showCompareViewer}
              onSearchClick={handleSearchClick}
            />
          )}
        </Allotment.Pane>,
      );
    }
    if (isSearchPaneOpen && shouldRenderMainViewerPane) {
      sizes.push(300);
      panes.push(
        <Allotment.Pane key="pane-search" minSize={200}>
          <div
            style={{
              height: '100%',
              backgroundColor: 'white',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Top bar with close button */}
            <div
              style={{
                height: '38px',
                backgroundColor: '#F7F7F8',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0 10px 0 16px',
                borderBottom: '1px solid #e0e0e0',
              }}
            >
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: '#6c757d',
                  lineHeight: 1,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {T.translate('generic.search')}
              </span>
              <IconButton
                size="small"
                onClick={handleCloseSearchPane}
                sx={{
                  padding: '4px',
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                }}
              >
                <Close fontSize="small" />
              </IconButton>
            </div>
            {/* Search content */}
            <div style={{ flex: 1, overflow: 'hidden', padding: '10px 0' }}>
              <SearchWithList
                searchInput={searchState.searchInput}
                searchResults={searchState.searchResults}
                isLoading={searchState.isLoading}
                error={searchState.searchError}
                selectedIndex={selectedSearchIndex}
                hasSearched={searchState.hasSearched}
                noResultsDelayPassed={noResultsDelayPassed}
                onSearchInputChange={handleSearchInputChange}
                onClearSearch={handleClearSearch}
                onResultClick={handleResultClick}
              />
            </div>
          </div>
        </Allotment.Pane>,
      );
    }
  }

  // Note: Avoid forcing remounts so children (e.g., ViewerWrapper) retain state across layout changes
  return (
    <FocusViewBridgeProvider>
      {isMobile ? (
        file ? (
          <ViewerWrapper fileId={file} fileType={fileType} isDocFinder={props.isDocFinder} />
        ) : (
          <TablePortion {...props} />
        )
      ) : (
        <>
          <style>
            {`
              .sash, .sash:hover, .sash::before {
                background-color: #E2E2E2 !important;
              }
              .sash-vertical::after {
                content: '...';
                writing-mode: vertical-rl;
                text-orientation: sideways;
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-30%, -50%);
                font-size: 30px;
                color: #888;
              }
            `}
          </style>
          <div
            className={`${props.isBatchUploadMapDataSidebarOpen ? 'content-shift' : ''}`}
            style={{ height: '100%', display: 'flex' }}
          >
            <Allotment
              defaultSizes={sizes}
              className={`custom-allotment ${props.isDocFinder ? 'no-animate' : ''}`}
              style={{
                '--separator-border': '1px solid #E2E2E2',
                '--separator-hover-border': '1px solid #E2E2E2',
              }}
            >
              {panes}
            </Allotment>
          </div>
        </>
      )}
    </FocusViewBridgeProvider>
  );
};

TableAndViewer.propTypes = {
  file: PropTypes.string,
  isSummaryOpen: PropTypes.bool,
  askAiSearchType: PropTypes.string,
  fileToCompare: PropTypes.shape({
    id: PropTypes.string,
    type: PropTypes.string,
    name: PropTypes.string,
  }),
  isAiTools: PropTypes.bool,
  isAskAIEnabled: PropTypes.bool,
  currentlySelectedRow: PropTypes.any,
  viewerControl: PropTypes.any,
  fileType: PropTypes.string,
  selectedDocument: PropTypes.object,
  actions: PropTypes.shape({
    fetchFileDetails: PropTypes.func,
  }),
  isDocFinder: PropTypes.bool,
  isRecycleBin: PropTypes.bool,
};

/* istanbul ignore next */
function mapStateToProps(state) {
  // It's gonna rerender due to other props, location, history, ...
  const {
    bundle,
    teamBundle,
    privateBundle,
    trialbook,
    courtBundle,
    groupBundle,
    quickLink,
    compositeKey,
    file,
  } = selectParams(state);
  const caseId = selectCurrentCaseId(state);
  const currentUrl = selectMatchedUrl(state);
  const currentlySelectedRow = selectCurrentSelectedFileMetaData(state);
  const isAiTools = selectUserAiTools(state);
  const showSearchMode = selectUserSettingSearchMode(state);
  const showDocFinder = selectUserSettingDocFinder(state);
  const showAllAnnotations = selectUserSettingAllAnnotations(state);
  const showPageRefs = selectUserSettingPageRefs(state);
  const isLeftSideBarOpen = selectGlobalSideBarOpen(state);
  const isSummaryOpen = selectGlobalSummaryOpen(state);
  const askAiSearchType = selectAskAiSearchType(state);
  const viewerControl = selectViewerControl(state);
  const fileToCompare = selectFileToCompare(state);
  const isRecycleBin = currentUrl?.includes(`${caseId}/recycle-bin`);
  const isBatchUploadMapDataSidebarOpen = selectIsLocalUploadDrawerOpen(state);
  return {
    isBundle: !!bundle,
    isTeamBundle: !!teamBundle,
    isPrivateBundle: !!privateBundle,
    isTrialbook: !!trialbook,
    isCourtBundle: !!courtBundle,
    isGroupBundle: !!groupBundle,
    isQuickLink: !!quickLink,
    file,
    compositeKey,
    fileType: selectedDocument ? selectedDocument.type : selectedAnnotation ? 'doc' : undefined,
    selectedDocument,
    isAskAIEnabled: isAiTools,
    isPresentMode: currentUrl.includes(`${caseId}/present-view`),
    isPresentModeWithBackEnd: false,
    isAiTools: isAiTools && currentUrl.includes(`${caseId}/ai-tools`),
    isSearchMode: showSearchMode && currentUrl.includes(`${caseId}/search-mode`),
    isDocFinder: showDocFinder && currentUrl.includes(`${caseId}/docFinder`),
    isRecycleBin,
    isAllAnnotations: showAllAnnotations && currentUrl.includes(`${caseId}/all-annotations`),
    isPageRefs: showPageRefs && currentUrl.includes(`${caseId}/page-refs`),
    hasDocFinderFilter: selectDocFinderFilter(state),
    currentlySelectedRow,
    quickLink,
    isLeftSideBarOpen,
    isSummaryOpen,
    askAiSearchType,
    fileToCompare,
    viewerControl,
    isBatchUploadMapDataSidebarOpen,
  };
}

/* istanbul ignore next */
function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ ...actions }, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(TableAndViewer);
